import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, ArrowLeft, Server, Database, FileText, Stethoscope, CheckCircle, RefreshCw, Shield } from 'lucide-react';

interface FlowStep {
  id: string;
  direction: 'pull' | 'push';
  source: string;
  target: string;
  dataType: string;
  fhirResource: string;
  status: 'active' | 'synced' | 'pending';
  lastSync: string;
  description: string;
}

const flows: FlowStep[] = [
  {
    id: '1', direction: 'pull', source: 'Epic EHR', target: 'LINK PRI',
    dataType: 'Surgical Schedule', fhirResource: 'Appointment / Schedule',
    status: 'synced', lastSync: '2 min ago',
    description: 'Pulls upcoming surgical cases to pre-populate authorization requests.',
  },
  {
    id: '2', direction: 'pull', source: 'Epic EHR', target: 'LINK PRI',
    dataType: 'Patient Demographics', fhirResource: 'Patient / Coverage',
    status: 'synced', lastSync: '2 min ago',
    description: 'Pulls patient identity and insurance coverage for payer routing.',
  },
  {
    id: '3', direction: 'pull', source: 'Epic EHR', target: 'LINK PRI',
    dataType: 'Diagnoses & Conditions', fhirResource: 'Condition',
    status: 'active', lastSync: '5 min ago',
    description: 'Pulls ICD-10 diagnoses to auto-map medical necessity.',
  },
  {
    id: '4', direction: 'push', source: 'LINK PRI', target: 'Epic EHR',
    dataType: 'PRI-Validated Operative Note', fhirResource: 'DocumentReference',
    status: 'active', lastSync: '12 min ago',
    description: 'Pushes the surgeon-attested, compliance-gated operative note back into the patient chart.',
  },
  {
    id: '5', direction: 'push', source: 'LINK PRI', target: 'Epic EHR',
    dataType: 'CPT Billing String', fhirResource: 'Claim / ChargeItem',
    status: 'active', lastSync: '12 min ago',
    description: 'Pushes the finalized CPT string with modifiers and authorization references.',
  },
  {
    id: '6', direction: 'push', source: 'LINK PRI', target: 'Epic EHR',
    dataType: 'Authorization Status', fhirResource: 'Task / InBasket',
    status: 'synced', lastSync: '1 min ago',
    description: 'Pushes auth status updates (approved, denied, pending) as InBasket messages or Tasks.',
  },
];

const statusColors: Record<string, string> = {
  active: 'text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30',
  synced: 'text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-950/30',
  pending: 'text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30',
};

export function FhirDataFlowPanel() {
  const pullFlows = flows.filter(f => f.direction === 'pull');
  const pushFlows = flows.filter(f => f.direction === 'push');

  return (
    <div className="space-y-4">
      {/* Header card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-primary" />
              <div>
                <p className="font-semibold text-sm">Bi-directional FHIR R4 Sync</p>
                <p className="text-xs text-muted-foreground">
                  LINK pulls Schedule & Patient Demographics — pushes PRI-Validated Operative Note & CPT String
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="gap-1 text-xs"><Shield className="w-3 h-3" />OAuth 2.0</Badge>
              <Badge variant="outline" className="gap-1 text-xs"><CheckCircle className="w-3 h-3" />SMART-on-FHIR</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Visual flow diagram */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-primary" />
            Clean-Room Data Flow
          </CardTitle>
          <CardDescription>How LINK interacts with your EHR — no PHI leaves the secure perimeter</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* PULL section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ArrowLeft className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-semibold">Inbound (EHR → LINK)</span>
              <Badge variant="secondary" className="text-[10px]">{pullFlows.length} resources</Badge>
            </div>
            <div className="space-y-2">
              {pullFlows.map(f => (
                <FlowCard key={f.id} flow={f} />
              ))}
            </div>
          </div>

          <Separator />

          {/* PUSH section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ArrowRight className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-semibold">Outbound (LINK → EHR)</span>
              <Badge variant="secondary" className="text-[10px]">{pushFlows.length} resources</Badge>
            </div>
            <div className="space-y-2">
              {pushFlows.map(f => (
                <FlowCard key={f.id} flow={f} />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FlowCard({ flow }: { flow: FlowStep }) {
  const DirectionIcon = flow.direction === 'pull' ? ArrowLeft : ArrowRight;
  const dirColor = flow.direction === 'pull' ? 'text-blue-500' : 'text-emerald-500';

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
      <DirectionIcon className={`w-4 h-4 shrink-0 ${dirColor}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{flow.dataType}</span>
          <Badge variant="outline" className="text-[10px] font-mono">{flow.fhirResource}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{flow.description}</p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <Badge variant="outline" className={`text-[10px] ${statusColors[flow.status]}`}>
          {flow.status === 'synced' ? 'Synced' : flow.status === 'active' ? 'Active' : 'Pending'}
        </Badge>
        <span className="text-[10px] text-muted-foreground">{flow.lastSync}</span>
      </div>
    </div>
  );
}
